import React, { useEffect, useState, useRef } from 'react';
import {
  Card, Row, Col, Typography, Input, InputNumber, Button, Divider, Space, Tooltip, Tag, Statistic,
  Drawer, Grid, message, Modal, Avatar
} from 'antd';
import {
  InfoCircleOutlined, BankOutlined, CheckCircleOutlined, LockOutlined, WarningOutlined, CalculatorOutlined,
  UserOutlined, CrownOutlined
} from '@ant-design/icons';
import { IoCashOutline, IoWalletOutline } from "react-icons/io5";
import { Link } from 'react-router-dom';
import LayoutHome from '../../components/layout/LayoutHome';
import { useVentasSesion } from '../../hooks/useVentas';
import { useCaja } from '../../hooks/useCaja';
import { useAuth } from '../../aws/AuthContext';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { TicketCierreCaja } from './TicketCierreCaja';
import { useConfig } from '../../context/ConfigContext';
import dayjs from 'dayjs';
import VentanaAbrirCaja from './ventanas/VentanaAbrirCaja';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const CierreCaja = () => {

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { getJwtToken, session } = useAuth();
  const { config } = useConfig();
  const nombreUsuario = session?.user?.nombre || 'Usuario';
  const apellidosUsuario = session?.user?.apellidos || '';

  const [baseCaja, setBaseCaja] = useState(0);
  const { getCajaActiva, abrirCaja, cerrarCaja } = useCaja();
  const cajaActiva = getCajaActiva.data;
  const isCajaAbierta = !!cajaActiva;
  const baseCajaActual = cajaActiva?.monto_inicial;
  const { data: historialVentas = [], isLoading: cargandoVentas } = useVentasSesion(cajaActiva?.id);
  const [historialAbonos, setHistorialAbonos] = useState([]);
  const [historialEgresos, setHistorialEgresos] = useState([]);
  const [cargandoAbonos, setCargandoAbonos] = useState(false);
  const [cargandoEgresos, setCargandoEgresos] = useState(false);
  const [efectivoContado, setEfectivoContado] = useState(null);
  const [observaciones, setObservaciones] = useState('');
  const [drawerArqueoVisible, setDrawerArqueoVisible] = useState(false);
  const [datosImpresionCierre, setDatosImpresionCierre] = useState(null);
  const [historialNotas, setHistorialNotas] = useState([]);

  const printComponentRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printComponentRef,
    documentTitle: "Cierre de Caja",
  });

  useEffect(() => {
    const fetchMovimientosExtra = async () => {
      if (!cajaActiva?.id) return;
      setCargandoAbonos(true);
      setCargandoEgresos(true);

      try {
        const token = await getJwtToken();
        const headers = { Authorization: `Bearer ${token}` };

        const resAbonos = axios.get(`${API_BASE_URL}/v1-abonos?sesion_caja_id=${cajaActiva.id}`, { headers });
        const resEgresos = axios.get(`${API_BASE_URL}/v1-caja-movimientos?sesion_caja_id=${cajaActiva.id}&tipo=egreso`, { headers });
        const resNotas = axios.get(`${API_BASE_URL}/v1-notas-credito`, { headers });

        const [abonosData, egresosData, notasData] = await Promise.all([resAbonos, resEgresos, resNotas]);

        setHistorialAbonos(abonosData.data || []);
        setHistorialEgresos(egresosData.data || []);

        const notasDeEsteTurno = (notasData.data || []).filter(nota =>
          dayjs(nota.created_at).isAfter(dayjs(cajaActiva.creado_at))
        );
        setHistorialNotas(notasDeEsteTurno);

      } catch (error) {
        console.error("Error cargando movimientos de caja:", error);
      } finally {
        setCargandoAbonos(false);
        setCargandoEgresos(false);
      }
    };

    fetchMovimientosExtra();
  }, [cajaActiva?.id]);

  const ventasEfectivo = historialVentas.reduce((suma, venta) => {
    const metodo = venta.metodo_pago?.toLowerCase();
    if (metodo === 'efectivo') {
      return suma + Number(venta.total || 0);
    } else if (metodo === 'mixto' || metodo === 'credito') {
      // Sumamos la parte física (aplica para pagos Mixtos y para Abonos Iniciales de Crédito)
      return suma + Number(venta.monto_efectivo || 0);
    }
    return suma;
  }, 0);

  const ventasTransferencia = historialVentas.reduce((suma, venta) => {
    const metodo = venta.metodo_pago?.toLowerCase();
    if (['transferencia', 'nequi', 'tarjeta'].includes(metodo)) {
      return suma + Number(venta.total || 0);
    } else if (metodo === 'mixto') {
      return suma + Number(venta.monto_transferencia || 0);
    }
    return suma;
  }, 0);

  const ventasCredito = historialVentas
    .filter(venta => venta.metodo_pago?.toLowerCase() === 'credito' || venta.status_pago === 'pendiente')
    .reduce((suma, venta) => suma + (Number(venta.total) - (Number(venta.monto_efectivo || 0) + Number(venta.monto_transferencia || 0))), 0);

  const totalVentas = historialVentas.reduce((suma, venta) => suma + Number(venta.total || 0), 0);

  const devolucionesTransferencia = historialNotas
    .filter(nota => ['transferencia', 'nequi', 'tarjeta'].includes(nota.metodo_pago?.toLowerCase()))
    .reduce((suma, nota) => suma + Number(nota.total_devolucion), 0);

  const abonosEfectivo = historialAbonos
    .filter(abono => abono.metodo_pago?.toLowerCase() === 'efectivo')
    .reduce((suma, abono) => suma + Number(abono.monto), 0);

  const abonosTransferencia = historialAbonos
    .filter(abono => ['transferencia', 'nequi', 'tarjeta'].includes(abono.metodo_pago?.toLowerCase()))
    .reduce((suma, abono) => suma + Number(abono.monto), 0);

  // Transferencias brutas (Ventas + Abonos)
  const transferenciasBrutas = (ventasTransferencia || 0) + (abonosTransferencia || 0);
  // Transferencias netas reales (quitando las devoluciones)
  const transferenciasNetas = transferenciasBrutas - devolucionesTransferencia;

  const pagosProveedoresEfectivo = historialEgresos
    .filter(egreso => egreso.categoria === 'pago_proveedor')
    .reduce((suma, egreso) => suma + Number(egreso.monto), 0);

  const gastosRegistrados = historialEgresos
    .filter(egreso => egreso.categoria === 'gasto_diario')
    .reduce((suma, egreso) => suma + Number(egreso.monto), 0);

  const devolucionesEfectivo = historialEgresos
    .filter(egreso => egreso.categoria === 'devolucion_venta')
    .reduce((suma, egreso) => suma + Number(egreso.monto), 0);

  const baseReal = Number(cajaActiva?.monto_inicial) || 0;
  const efectivoEsperado =
    baseReal
    + ventasEfectivo
    + abonosEfectivo
    - pagosProveedoresEfectivo
    - gastosRegistrados
    - devolucionesEfectivo;

  const haIngresadoEfectivo = efectivoContado !== null && efectivoContado !== undefined;
  const diferencia = haIngresadoEfectivo ? (efectivoContado - efectivoEsperado) : 0;

  const planActual = session?.empresa?.plan_actual || 'basico';

  const handleAbrirCaja = (bodegaIdAsignada) => {
    if (baseCaja >= 0) {
      abrirCaja.mutate({ monto_inicial: baseCaja, bodega_id: bodegaIdAsignada }, {
        onSuccess: () => {
          message.success("Jornada iniciada con éxito");
        },
        onError: (err) => {
          const errorMsg = err.response?.data?.error || "";
          const usuarioCaja = err.response?.data?.usuario_caja || "Otro empleado";

          if (err.response?.status === 403 || errorMsg === 'limite_plan_basico') {

            Modal.info({
              icon: null,
              width: 480,
              centered: true,
              closable: true,
              footer: null,
              content: (
                <div style={{ textAlign: 'center', marginTop: '10px' }}>
                  <div style={{ background: '#fffbe6', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <WarningOutlined style={{ fontSize: '32px', color: '#faad14' }} />
                  </div>
                  <Title level={4} style={{ margin: '0 0 10px 0', color: '#141414' }}>
                    Límite de Cajas Alcanzado
                  </Title>
                  <Text style={{ fontSize: '15px', color: '#595959', display: 'block', padding: '0 10px' }}>
                    Tu <b>Plan Básico</b> solo permite tener <b>1 caja registradora</b> abierta al mismo tiempo en todo el negocio.
                  </Text>

                  <div style={{
                    background: '#f8fafc', padding: '16px', borderRadius: '12px', margin: '24px 0',
                    display: 'flex', alignItems: 'center', gap: '16px', textAlign: 'left', border: '1px solid #e2e8f0'
                  }}>
                    <Avatar size={48} icon={<UserOutlined />} style={{ backgroundColor: '#cbd5e1', color: '#475569' }} />
                    <div>
                      <Text style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                        CAJA ACTUALMENTE EN USO POR:
                      </Text>
                      <Text strong style={{ fontSize: '16px', color: '#0f172a' }}>
                        {usuarioCaja}
                      </Text>
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce3 100%)',
                    padding: '24px', borderRadius: '16px', border: '1px solid #bbf7d0', textAlign: 'center'
                  }}>
                    <CrownOutlined style={{ fontSize: '32px', color: '#16a34a', marginBottom: '12px' }} />
                    <Text strong style={{ display: 'block', fontSize: '17px', color: '#166534', marginBottom: '8px' }}>
                      ¿Necesitas facturar a dos manos?
                    </Text>
                    <Text style={{ fontSize: '13px', color: '#14532d', display: 'block', marginBottom: '20px' }}>
                      Mejora al <b>Plan Premium</b> para habilitar múltiples cajas simultáneas, control avanzado y App Móvil.
                    </Text>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <Button size="large" onClick={() => Modal.destroyAll()} style={{ flex: 1, borderRadius: '8px', fontWeight: '500' }}>
                        Entendido
                      </Button>
                    </div>
                  </div>

                </div>
              )
            });

          } else {
            message.error(errorMsg || "Error de conexión al intentar abrir la caja.");
          }
        }
      });
    }
  };

  const handleCerrarCaja = () => {
    const payloadCierre = {
      id: cajaActiva.id,
      monto_esperado: efectivoEsperado,
      monto_fisico: efectivoContado,
      diferencia: diferencia,
      observaciones: observaciones
    };

    cerrarCaja.mutate(payloadCierre, {
      onSuccess: () => {
        // Preparamos los datos para el ticket ANTES de limpiar el estado
        setDatosImpresionCierre({
          id: cajaActiva.id,
          nombreUsuario: nombreUsuario + ' ' + apellidosUsuario,
          creado_at: cajaActiva.creado_at
            ? dayjs(cajaActiva.creado_at).format('DD/MM/YYYY hh:mm A')
            : 'N/A',
          cierre: dayjs().format('DD/MM/YYYY hh:mm A'),
          base_inicial: baseReal,
          ventas_totales: totalVentas,
          ventas_efectivo: ventasEfectivo,
          abonos_efectivo: abonosEfectivo,
          ventas_transferencia: transferenciasBrutas,
          abonos_transferencia: abonosTransferencia,
          ventas_credito: ventasCredito,
          devoluciones_transferencia: devolucionesTransferencia,
          pagos_proveedores: pagosProveedoresEfectivo,
          gastos: gastosRegistrados,
          devoluciones: devolucionesEfectivo,
          monto_esperado: efectivoEsperado,
          monto_fisico: efectivoContado,
          diferencia: diferencia,
          observaciones: observaciones
        });

        message.success("Turno finalizado con éxito. Imprimiendo reporte...");
        setEfectivoContado(0);
        setObservaciones('');
        if (isMobile) setDrawerArqueoVisible(false);

        // Lanzamos la impresión con un pequeño retraso para asegurar el render del componente oculto
        setTimeout(() => {
          handlePrint();
        }, 500);
      }
    });
  };

  // --- COMPONENTE INTERNO: ARQUEO MANUAL ---
  // Cambiamos el nombre para usarlo como función y evitar que React desmonte el Input
  const renderArqueoManual = () => (
    <Card bordered={false} style={{ borderRadius: '12px', background: '#001529', color: '#fff', height: '100%' }}>
      <Title level={4} style={{ color: '#fff' }}>Arqueo Manual</Title>
      <Text style={{ color: 'rgba(255,255,255,0.7)' }}>Cuenta el dinero físico e ingresa el total:</Text>

      <InputNumber
        value={efectivoContado}
        size="large"
        style={{ width: '100%', marginTop: '20px', height: '60px', display: 'flex', alignItems: 'center', fontSize: '24px' }}
        placeholder="$ 0"
        onChange={(val) => setEfectivoContado(val)}
        formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={value => value.replace(/\$\s?|,/g, '')}
      />

      <div style={{
        marginTop: '25px', padding: '15px', borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center', background: 'rgba(255,255,255,0.03)'
      }}>
        <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>Diferencia contra sistema:</Text>

        <div style={{ fontSize: '32px', fontWeight: 'bold', color: diferencia === 0 ? '#52c41a' : (diferencia > 0 ? '#1890ff' : '#ff4d4f'), margin: '5px 0' }}>
          {diferencia > 0 ? `+ $${Number(diferencia).toLocaleString('es-CO')}` : diferencia < 0 ? `- $${Number(Math.abs(diferencia)).toLocaleString('es-CO')}` : `$0`}
        </div>

        {diferencia === 0 && <Tag color="success" icon={<CheckCircleOutlined />}>CAJA CUADRADA</Tag>}
        {diferencia > 0 && <Tag color="processing" icon={<InfoCircleOutlined />}>SOBRANTE DE DINERO</Tag>}
        {diferencia < 0 && <Tag color="error" icon={<WarningOutlined />}>FALTANTE DE DINERO</Tag>}

        {diferencia !== 0 && (
          <div style={{ marginTop: '10px' }}>
            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontStyle: 'italic' }}>
              {diferencia > 0 ? "Verifica si olvidaste registrar alguna venta o ingreso." : "Verifica si olvidaste registrar algún gasto o diste mal un cambio."}
            </Text>
          </div>
        )}
      </div>

      <div style={{ marginTop: '20px' }}>
        <Text style={{ color: '#fff' }}>Observaciones:</Text>
        <Input.TextArea
          rows={3}
          style={{ marginTop: '8px', background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
          placeholder="Justifica aquí cualquier novedad..."
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </div>

      <Button
        type="primary"
        size="large"
        block
        icon={<LockOutlined />}
        onClick={handleCerrarCaja}
        loading={cerrarCaja.isLoading}
        style={{ height: '55px', marginTop: '25px', backgroundColor: '#22BABB', border: 'none', fontWeight: 'bold' }}
        disabled={!haIngresadoEfectivo || (diferencia !== 0 && observaciones.length < 4)}
      >
        FINALIZAR Y CERRAR
      </Button>
    </Card>
  );

  if (!isCajaAbierta) {
    return (
      <>

        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <div ref={printComponentRef} style={{ display: 'flex', justifyContent: 'center', minHeight: '100vh', padding: '20px', backgroundColor: '#F0F2F4' }}>
            {datosImpresionCierre && (
              <TicketCierreCaja
                datosCierre={datosImpresionCierre}
                infoNegocio={{
                  nombre: config.nombre,
                  nit: config.identificacion_fiscal,
                  anchoTicket: config.settings?.impresora_tipo
                }}
              />
            )}
          </div>
        </div>

        <VentanaAbrirCaja setBaseCaja={setBaseCaja} handleAbrirCaja={handleAbrirCaja} />
      </>
    );
  }

  return (
    <>
      <LayoutHome pageSeleccionada="cierre-caja">
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <Title level={3} style={{ margin: 0, color: '#001529' }}>Cierre de Caja</Title>
              <Tag color="green" icon={<CheckCircleOutlined />}>CAJA ABIERTA</Tag>
            </div>
            <Text type="secondary">
              Iniciado: {cajaActiva?.creado_at
                ? new Date(cajaActiva.creado_at).toLocaleString('es-CO', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', hour12: true
                })
                : 'Cargando...'}
            </Text>
          </div>

          <Row gutter={[20, 20]}>
            {/* COLUMNA IZQUIERDA: RESUMEN (Toma todo el ancho en móvil) */}
            <Col xs={24} lg={16}>
              <Card title={<Space><IoCashOutline /> Resumen de Dinero (Sistema)</Space>} bordered={false} style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }} styles={{ body: { padding: isMobile ? '12px' : '24px' } }}>
                <Row gutter={[16, 16]}>
                  <Col xs={12} md={8}>
                    <Statistic
                      title={<Space>Base inicial <Tooltip title="Efectivo con el que inicio la jornada."><InfoCircleOutlined /></Tooltip></Space>}
                      value={Number(baseCajaActual || 0).toLocaleString('es-CO')} prefix="$" loading={cargandoVentas}
                    />
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic
                      title={<Space>Total de Ventas <Tooltip title="Total acumulado de ventas incluyendo efectivo, transferencias y créditos."><InfoCircleOutlined /></Tooltip></Space>}
                      value={Number(totalVentas || 0).toLocaleString('es-CO')} prefix="$" valueStyle={{ color: '#22BABB' }} loading={cargandoVentas}
                    />
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic
                      title={<Space>Recaudo Cartera <Tooltip title="Dinero que entró hoy por pagos de deudas atrasadas."><InfoCircleOutlined /></Tooltip></Space>}
                      value={Number(abonosEfectivo + abonosTransferencia || 0).toLocaleString('es-CO')} prefix="$" valueStyle={{ color: '#52c41a' }} loading={cargandoAbonos}
                    />
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic
                      title={<Space>Gastos Operativos</Space>}
                      value={Number(gastosRegistrados || 0).toLocaleString('es-CO')} prefix="-$" valueStyle={{ color: '#ff4d4f' }}
                    />
                    <Link to="/gastos" style={{ fontSize: '11px' }}>Ver Gastos</Link>
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic
                      title={<Space>Devoluciones a Clientes <Tooltip title="Dinero físico que se devolvió hoy a clientes por anulación de facturas."><InfoCircleOutlined /></Tooltip></Space>}
                      value={Number(devolucionesEfectivo || 0).toLocaleString('es-CO')} prefix="-$" valueStyle={{ color: '#eb2f96' }}
                    />
                    <Link to="/facturacion" style={{ fontSize: '11px' }}>Ver Notas Crédito</Link>
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic
                      title={<Space>Compras y Abonos <Tooltip title="Dinero físico que salió de esta caja hoy para comprar mercancía de contado o pagar deudas atrasadas."><InfoCircleOutlined /></Tooltip></Space>}
                      value={Number(pagosProveedoresEfectivo || 0).toLocaleString('es-CO')} prefix="-$" valueStyle={{ color: '#fa8c16' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', fontSize: '11px', marginTop: '4px' }}>
                      <Link to="/compras">Ver Compras</Link>
                      <Link to="/proveedores">Ver Proveedores</Link>
                    </div>
                  </Col>
                </Row>

                <Divider dashed />

                <Row align="middle" justify="space-between" style={{ background: '#f8fafc', padding: isMobile ? '10px' : '15px', borderRadius: '8px' }}>
                  <Col xs={24} md={16}>
                    <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>Efectivo Total Esperado</Title>
                    <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>
                      Base (${Number(baseCajaActual || 0).toLocaleString('es-CO')})
                      + Ventas Efec. (${Number(ventasEfectivo || 0).toLocaleString('es-CO')})
                      + Abonos Efec. (${Number(abonosEfectivo || 0).toLocaleString('es-CO')})
                      - Compras/Abonos (${Number(pagosProveedoresEfectivo).toLocaleString('es-CO')})
                      - Gastos (${Number(gastosRegistrados || 0).toLocaleString('es-CO')})
                      - Devoluciones en efectivo (${Number(devolucionesEfectivo || 0).toLocaleString('es-CO')})
                    </Text>
                  </Col>
                  <Col xs={24} md={8} style={{ textAlign: isMobile ? 'left' : 'right', marginTop: isMobile ? '10px' : '0' }}>
                    <Title level={2} style={{ margin: 0, color: '#22BABB' }}>
                      $ {Number(efectivoEsperado || 0).toLocaleString('es-CO')}
                    </Title>
                  </Col>
                </Row>
              </Card>

              <Card title="Otros Medios de Pago (Auditables)" style={{ marginTop: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: isMobile ? '70px' : '0' }} styles={{ body: { padding: isMobile ? '12px' : '24px' } }}>
                <Row gutter={[16, 16]}>
                  <Col xs={12} md={8}>
                    <Statistic
                      title="Transferencias / Nequi"
                      value={Number(transferenciasNetas || 0).toLocaleString('es-CO')}
                      prefix={<BankOutlined />}
                      valueStyle={{ fontSize: isMobile ? '16px' : '18px' }}
                    />
                    {/* Aviso visual de devoluciones electrónicas */}
                    {devolucionesTransferencia > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        <Text style={{ fontSize: '11px', color: '#ff4d4f', fontWeight: '500' }}>
                          (-${Number(devolucionesTransferencia).toLocaleString('es-CO')} devueltos)
                        </Text>
                      </div>
                    )}
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic
                      title="Créditos / Fiados"
                      value={Number(ventasCredito || 0).toLocaleString('es-CO')}
                      prefix={<IoWalletOutline />} valueStyle={{ fontSize: isMobile ? '16px' : '18px', color: '#faad14' }} loading={cargandoVentas}
                    />
                    <Link to="/clientes" style={{ fontSize: '11px' }}>Ver cuentas por cobrar</Link>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* COLUMNA DERECHA: ARQUEO MANUAL (Solo visible en Desktop) */}
            {!isMobile && (
              <Col xs={24} lg={8}>
                {renderArqueoManual()}
              </Col>
            )}
          </Row>
        </div>
      </LayoutHome>

      {/* DRAWER PARA ARQUEO MANUAL (Solo visible en Móvil) */}
      <Drawer
        title="Arqueo y Cierre"
        placement="bottom"
        height="85vh"
        open={drawerArqueoVisible}
        onClose={() => setDrawerArqueoVisible(false)}
        styles={{ body: { padding: 0, backgroundColor: '#001529' }, header: { backgroundColor: '#001529', borderBottom: '1px solid rgba(255,255,255,0.1)' } }}
      >
        {renderArqueoManual()}
      </Drawer>

      {/* BOTÓN FLOTANTE PARA ABRIR DRAWER (Solo visible en Móvil) */}
      {isMobile && isCajaAbierta && (
        <div style={{ position: 'fixed', bottom: '15px', left: '15px', right: '15px', zIndex: 1000 }}>
          <Button
            type="primary"
            size="large"
            block
            icon={<CalculatorOutlined />}
            onClick={() => setDrawerArqueoVisible(true)}
            style={{ height: '55px', backgroundColor: '#22BABB', borderRadius: '12px', fontWeight: 'bold', boxShadow: '0 4px 12px rgba(34, 186, 187, 0.4)' }}
          >
            HACER ARQUEO Y CERRAR
          </Button>
        </div>
      )}
    </>
  );
};

export default CierreCaja;